import type { ErrorBody } from './contracts.ts'

/** Ошибка API: `code` стабилен и нужен интерфейсу, `message` для человека. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Сеть или прокси: API недоступен, а не отказал. */
  get isUnavailable(): boolean {
    return this.status === 0 || this.status === 502 || this.status === 503 || this.status === 504
  }
}

export interface ApiClientOptions {
  /** Например `/api/v1`: web и admin ходят в API своего домена. */
  baseUrl: string
  getAccessToken: () => string | null
  /** Обновить access-токен после 401. `false` — сессии нет, повторять запрос незачем. */
  refreshAccessToken?: () => Promise<boolean>
  headers?: Readonly<Record<string, string>>
  fetch?: typeof fetch
}

export interface RequestOptions {
  headers?: Readonly<Record<string, string>>
  signal?: AbortSignal
  /** Без токена и без повтора: вход и обновление сессии. */
  anonymous?: boolean
}

export interface ApiClient {
  get: <T>(path: string, options?: RequestOptions) => Promise<T>
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => Promise<T>
  delete: <T>(path: string, options?: RequestOptions) => Promise<T>
  /** GET без разбора тела: для потоков (SSE). Ошибка статуса — `ApiError`, как у остальных. */
  open: (path: string, options?: RequestOptions) => Promise<Response>
}

function isErrorBody(value: unknown): value is ErrorBody {
  return typeof value === 'object' && value !== null && 'code' in value && typeof value.code === 'string'
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined
  const type = response.headers.get('content-type') ?? ''
  if (!type.includes('application/json')) return undefined
  try {
    return await response.json()
  } catch {
    return undefined
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? ((input, init) => fetch(input, init))

  async function send(
    method: string,
    path: string,
    body: unknown,
    request: RequestOptions,
    retried: boolean,
    raw = false,
  ): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json', ...options.headers, ...request.headers }
    if (body !== undefined) headers['content-type'] = 'application/json'
    const token = request.anonymous === true ? null : options.getAccessToken()
    if (token !== null) headers.authorization = `Bearer ${token}`

    let response: Response
    try {
      response = await doFetch(`${options.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: request.signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new ApiError(0, 'network_error', 'Нет связи с сервером')
    }

    if (response.status === 401 && request.anonymous !== true && !retried && options.refreshAccessToken) {
      if (await options.refreshAccessToken()) return send(method, path, body, request, true, raw)
    }

    if (raw && response.ok) return response
    const parsed = await parseBody(response)
    if (!response.ok) {
      if (isErrorBody(parsed)) {
        const details = 'details' in parsed ? parsed.details : undefined
        throw new ApiError(response.status, parsed.code, parsed.message, details)
      }
      throw new ApiError(response.status, 'http_error', response.statusText || 'Ошибка сервера')
    }
    return parsed
  }

  // Граница с сетью: форму ответа задают контракты API, здесь она не проверяется.
  async function call<T>(method: string, path: string, body: unknown, request: RequestOptions = {}): Promise<T> {
    return (await send(method, path, body, request, false)) as T
  }

  return {
    get: (path, request) => call('GET', path, undefined, request),
    post: (path, body, request) => call('POST', path, body, request),
    patch: (path, body, request) => call('PATCH', path, body, request),
    put: (path, body, request) => call('PUT', path, body, request),
    delete: (path, request) => call('DELETE', path, undefined, request),
    open: async (path, request = {}) => {
      const response = await send('GET', path, undefined, request, false, true)
      if (!(response instanceof Response)) throw new ApiError(0, 'stream_unavailable', 'Поток недоступен')
      return response
    },
  }
}
